import { z } from 'zod'

import { parseAmount } from '@/features/cash/item-form'
import { messages } from '@/lib/messages'
import type { CashManualItem, CashManualItemWrite } from '@/services/http/cash'

const t = messages.cash.manual

/** Boş ya da 1–31 arası tam sayı (`CashManualItemWrite.day`: `ge=1, le=31`, isteğe bağlı). */
function validDay(text: string): boolean {
  const trimmed = text.trim()
  if (!trimmed) return true
  const value = Number(trimmed)
  return Number.isInteger(value) && value >= 1 && value <= 31
}

/**
 * ERP dışı kalem editörünün form değerleri. Alan adları `CashManualItemWrite` ile aynıdır, böylece
 * sunucunun 422 yolu (`amount`, `note`, `day` ...) eşleme olmadan alana düşer. Sayılar ve tarihler
 * metin olarak tutulur (boş = yok); gövdeye `manualItemBody` çevirir.
 */
export const manualItemSchema = z
  .object({
    source: z.enum(['duzenli', 'tek']),
    type: z.string().min(1, t.typeRequired),
    description: z.string().max(300, t.descriptionTooLong),
    instrument: z.string().max(100),
    day: z.string().refine(validDay, t.dayInvalid),
    period: z.string(),
    date: z.string(),
    amount: z.string().refine((value) => parseAmount(value) > 0, t.amountInvalid),
    cari_code: z.string().max(50, t.cariCodeTooLong),
    // Ekranda düzenlenmez; kalemdeki değer olduğu gibi geri gönderilir.
    status: z.string().nullable(),
    note: z.string().max(1000, t.noteTooLong),
  })
  .superRefine((values, ctx) => {
    if (values.source === 'duzenli' && !values.period) {
      ctx.addIssue({ code: 'custom', path: ['period'], message: t.periodRequired })
    }
    if (values.source === 'tek' && !values.date) {
      ctx.addIssue({ code: 'custom', path: ['date'], message: t.dateRequired })
    }
  })

export type ManualItemFormValues = z.infer<typeof manualItemSchema>

export function emptyManualItemValues(type: string): ManualItemFormValues {
  return {
    source: 'duzenli',
    type,
    description: '',
    instrument: '',
    day: '',
    period: '',
    date: '',
    amount: '',
    cari_code: '',
    status: null,
    note: '',
  }
}

export function manualItemValues(item: CashManualItem): ManualItemFormValues {
  return {
    source: item.source,
    type: item.type,
    description: item.description ?? '',
    instrument: item.instrument ?? '',
    day: item.day == null ? '' : String(item.day),
    period: item.period ?? '',
    date: item.date ?? '',
    amount: String(item.amount),
    cari_code: item.cari_code ?? '',
    status: item.status ?? null,
    note: item.note ?? '',
  }
}

/**
 * Kaydet gövdesi. Güncellemede okunan sürüm (`version`) zorunludur, oluşturmada gönderilmez.
 * Kaynağa ait olmayan alanlar boş gider (sunucu zaten yok sayar): düzenlide tarih, tek seferlikte
 * dönem ve gün.
 */
export function manualItemBody(
  firm: string,
  values: ManualItemFormValues,
  version?: number,
): CashManualItemWrite & { version?: number } {
  const recurring = values.source === 'duzenli'
  const day = values.day.trim()
  return {
    firm,
    source: values.source,
    type: values.type,
    description: values.description,
    instrument: values.instrument,
    day: recurring && day ? Number(day) : null,
    period: recurring ? values.period || null : null,
    date: recurring ? null : values.date || null,
    amount: parseAmount(values.amount),
    cari_code: values.cari_code || null,
    status: values.status,
    note: values.note || null,
    ...(version === undefined ? {} : { version }),
  }
}
